// components/People/addPeopleToNode.tsx
import React, { useState } from "react";
import { createRoot } from "react-dom/client";
import PeoplePickerDialog from "components/Flow/Controls/PeoplePickerDialog";
import { Person } from "api/Community/CommunityInterfaces";

/**
 * Opens a dialog that lets the user select people
 * and adds them to a node field (same behavior as AddPeopleButton)
 */
export function addPeopleToNode(params: {
  nodeId: number;
  fieldName: string; // e.g. "people"
  peopleAPI: { getAll: () => Promise<any[]> };
  nodeAPI: { updateField: (id: number, field: string, value: any) => Promise<any> };
  currentPeople: any[];
  onAdded?: () => void;
}) {
  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const CleanupWrapper = () => {
    const [open, setOpen] = useState(true);
    const [allPeople, setAllPeople] = useState<Person[]>([]);
    const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);

    const handleClose = () => {
      setOpen(false);
      setTimeout(() => {
        root.unmount();
        container.remove();
      }, 200);
    };

    React.useEffect(() => {
      params.peopleAPI.getAll().then((res) => setAllPeople(res || []));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const existingIds = params.currentPeople
      .map((p: any) => (typeof p === "object" ? p.id : p))
      .filter((id): id is number => typeof id === "number");

    const existingIdSet = new Set(existingIds);

    const handleAdd = async () => {
      if (selectedPeople.length === 0) return;

      const newIds = selectedPeople
        .map((p: any) => p.id)
        .filter((id): id is number => typeof id === "number");

      const mergedIds = Array.from(new Set([...existingIds, ...newIds]));

      await params.nodeAPI.updateField(params.nodeId, params.fieldName, mergedIds);

      handleClose();
      params.onAdded?.();
    };

    return (
      <PeoplePickerDialog
        open={open}
        title="Select People to Add"
        people={allPeople}
        selected={selectedPeople}
        onSelectedChange={setSelectedPeople}
        disabledIds={Array.from(existingIdSet)}
        confirmLabel="Add Selected People"
        onClose={handleClose}
        onConfirm={handleAdd}
        helperText="Select one or more people to add, or create a new person."
      />
    );
  };

  root.render(<CleanupWrapper />);
}
